import React, { useState, useEffect } from 'react';
import styles from '../Styles/HcwAIChatHistory.module.css';
import formatTimestamp from '../Functions/formatTimestamp';
import { useAuth } from '../Components/AuthContext';

const HcwAIChatHistory = () => {
    const [rooms, setRooms] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(false);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [errorRooms, setErrorRooms] = useState(null);
    const [errorMsgs, setErrorMsgs] = useState(null);

    const url = process.env.REACT_APP_API_URL;
    const { token } = useAuth();

    // 1) load all chat rooms
    useEffect(() => {
        const fetchRooms = async () => {
            setLoadingRooms(true);
            setErrorRooms(null);
            try {
                const res = await fetch(`${url}/chat/ai-chatrooms`, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!res.ok) throw new Error('Failed to fetch chat rooms');
                const data = await res.json();
                setRooms(data);
                if (data.length) setSelectedId(data[0].id);
            } catch (err) {
                console.error(err);
                setErrorRooms('Error loading conversations');
            } finally {
                setLoadingRooms(false);
            }
        };
        fetchRooms();
    }, [url, token]);

    // 2) when a room is selected, load its messages
    useEffect(() => {
        if (selectedId === null) return;
        const fetchMessages = async () => {
            setLoadingMsgs(true);
            setErrorMsgs(null);
            try {
                const res = await fetch(
                    `${url}/chat/ai-chatroom/${selectedId}`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (!res.ok) throw new Error('Failed to fetch messages');
                const { messages: msgs } = await res.json();
                // sort by timestamp ascending
                const sorted = msgs
                    .slice()
                    .sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                    );

                setMessages(sorted);
            } catch (err) {
                console.error(err);
                setErrorMsgs('Error loading messages');
            } finally {
                setLoadingMsgs(false);
            }
        };
        fetchMessages();
    }, [selectedId, url, token]);

    const currentRoom = rooms.find((r) => r.id === selectedId);

    return (
        <div className={styles.container}>
            {/* Sidebar */}
            <aside className={styles.sidebar}>
                <div className={styles.conversationHeader}>
                    <h2>Conversations</h2>
                </div>

                {loadingRooms ? (
                    <p>Loading…</p>
                ) : errorRooms ? (
                    <p className={styles.error}>{errorRooms}</p>
                ) : (
                    <ul className={styles.conversationList}>
                        {rooms.map((room) => (
                            <li
                                key={room.id}
                                className={`${styles.conversationItem} ${
                                    room.id === selectedId
                                        ? styles.selected
                                        : ''
                                }`}
                                onClick={() => setSelectedId(room.id)}
                            >
                                {room.name}
                            </li>
                        ))}
                    </ul>
                )}
            </aside>

            {/* Chat window */}
            <section className={styles.chatWindow}>
                <div className={styles.chatHeader}>
                    <h2>
                        {currentRoom
                            ? `${currentRoom.name}`
                            : 'Select a conversation'}
                    </h2>
                </div>

                {loadingMsgs ? (
                    <p>Loading messages…</p>
                ) : errorMsgs ? (
                    <p className={styles.error}>{errorMsgs}</p>
                ) : !currentRoom ? (
                    <div className={styles.noConversationSelected}>
                        No conversation selected.
                    </div>
                ) : (
                    <div className={styles.chatMessages}>
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`${styles.message} ${
                                    msg.sender?.toLowerCase() === 'hcw'
                                        ? styles.myMessage
                                        : styles.otherMessage
                                }`}
                            >
                                <div className={styles.messageContent}>
                                    {msg.message}
                                </div>
                                <div
                                    className={`${styles.messageMeta} ${
                                        msg.sender?.toLowerCase() === 'hcw'
                                            ? styles.metaRight
                                            : styles.metaLeft
                                    }`}
                                >
                                    {formatTimestamp(msg.timestamp)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default HcwAIChatHistory;
